import React from 'react';
import CCPlayerTheater from '../../CCPlayer/CCPlayerTheater';

/**
 * VideoPlayer — the full-page media viewer's video player, now a thin wrapper
 * over the unified CCPlayer Theater preset (CEL-1157 Phase B; design doc
 * agent_docs/cel1157_tutorials_hub_design.md §5.2).
 *
 * The ~700-line bespoke implementation this file used to carry was absorbed
 * into CCPlayer's hooks (useVideoEngine/useScrub/useMilestones + the icons) —
 * one player codebase instead of 3.5. What the viewer GAINS for free:
 * captions support (when a track URL is ever passed) and the shared,
 * hardened control chrome. What is PRESERVED, verbatim, for the 2 consumers
 * (FullPageMediaViewer + MediaViewerPanel):
 *   src · filename · fileSize · isDarkMode · videoRef · initialTimestamp ·
 *   autoPlay · onTimeUpdate · disableKeyboard
 * Chat-file videos have no VTT and no analytics context — CCPlayer tracks
 * nothing when analyticsContext is omitted (by design; the viewer is a
 * private logged-in surface).
 *
 * `filename`/`fileSize` are accepted for contract compatibility; the title
 * feeds the aria-label (the info bar above the player still renders them).
 */
const VideoPlayer = ({
  src,
  filename,
  fileSize, // eslint-disable-line no-unused-vars -- contract compat; rendered by MediaInfoBar upstream
  isDarkMode,
  videoRef,
  initialTimestamp = 0,
  autoPlay = false,
  onTimeUpdate,
  disableKeyboard = false,
}) => {
  // LATCH the autoplay decision ONCE per opened video. `initialTimestamp` is
  // live resume-position feedback (the panel records position while the video
  // plays), so computing autoplay from it on every render flipped the prop
  // mid-playback — which re-ran the player's per-source reset and put the
  // poster play button back over a playing video (the recurring bug).
  // Old contract preserved, decided once per `src`: a fresh open (no resume
  // timestamp) auto-plays; a resume state plays only if it was playing.
  const latch = React.useRef({ src: null, value: false });
  if (latch.current.src !== src) {
    latch.current = { src, value: autoPlay || initialTimestamp === 0 };
  }
  return (
    <CCPlayerTheater
      src={src}
      title={filename}
      theme={isDarkMode ? 'dark' : 'light'}
      // FIT mode: the viewer hugs the video at its intrinsic aspect and centers
      // it (portrait fills the height) — the old sizing contract, restored
      // after Nitish's review caught the fixed-16:9 regression.
      fit
      videoRef={videoRef}
      initialTimestamp={initialTimestamp}
      autoPlay={latch.current.value}
      onTimeUpdate={onTimeUpdate}
      disableKeyboard={disableKeyboard}
    />
  );
};

export default VideoPlayer;
